import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  buttonBlue,
  cardBorderGrey,
  pageBackgroundGrey,
} from 'assets/styles/variables'
import useThemes from 'features/project/hooks/use-themes'
import mixpanel from 'trackers/mixpanel'
type Props = {
  setActiveValues: (values: string[] | ((prev: string[]) => string[])) => void
  options: string[]
  subthemeName: string | null
  setSubthemeValue: (value: string | null) => void
  themeName: string
}
const ThemeSelector: React.FC<Props> = ({
  setActiveValues,
  options,
  subthemeName,
  setSubthemeValue,
  themeName,
}) => {
  const { getSubthemeNameFromClusterUUID, getClusterUUIDsFromThemeName } =
    useThemes()

  const optionsKey = useMemo(() => options.join('|'), [options])
  const selectedSubthemeLabel = subthemeName?.trim().toLowerCase() ?? null
  const [focusedSubthemeLabel, setFocusedSubthemeLabel] = useState<
    string | null
  >(selectedSubthemeLabel)

  const setActiveValuesIfChanged = useCallback(
    (nextValues: string[]) => {
      setActiveValues((prevValues) => {
        if (
          prevValues.length === nextValues.length &&
          prevValues.every((value, index) => value === nextValues[index])
        ) {
          return prevValues
        }
        return nextValues
      })
    },
    [setActiveValues],
  )

  useEffect(() => {
    setFocusedSubthemeLabel(selectedSubthemeLabel)
  }, [selectedSubthemeLabel])

  useEffect(() => {
    if (!selectedSubthemeLabel) {
      setActiveValuesIfChanged(options)
      return
    }
    setActiveValuesIfChanged(
      getClusterUUIDsFromThemeName(themeName, selectedSubthemeLabel),
    )
  }, [
    getClusterUUIDsFromThemeName,
    optionsKey,
    selectedSubthemeLabel,
    themeName,
    options,
    setActiveValuesIfChanged,
  ])

  const subthemes = useMemo(() => {
    const uniqueOptions = Array.from(new Set(options))
    const seenLabels = new Set<string>()
    const items: Array<{ label: string; uuid: string }> = []

    uniqueOptions.forEach((uuid) => {
      const subtheme = getSubthemeNameFromClusterUUID(uuid)
      if (!subtheme) {
        return
      }
      const normalizedLabel = subtheme.trim().toLowerCase()
      if (seenLabels.has(normalizedLabel)) {
        return
      }
      seenLabels.add(normalizedLabel)
      items.push({ label: subtheme, uuid })
    })
    return items
  }, [getSubthemeNameFromClusterUUID, options])

  const isActiveSubtheme = (label: string): boolean => {
    if (!focusedSubthemeLabel) {
      return false
    }
    const normalizedLabel = label.trim().toLowerCase()
    return normalizedLabel === focusedSubthemeLabel
  }

  return (
    <Container>
      <ProductItemContainer>
        {subthemes.map(({ label, uuid }) => {
          const isSelected = isActiveSubtheme(label)
          return (
            <ProductItem
              key={uuid}
              id={'uuid-' + uuid}
              onClick={() => {
                mixpanel.track('catalog', {
                  action: 'select subtheme',
                  lens: 'theme',
                  value: uuid,
                })
                if (isSelected) {
                  setFocusedSubthemeLabel(null)
                  setActiveValuesIfChanged(options)
                  setSubthemeValue(null)
                } else {
                  setFocusedSubthemeLabel(label.trim().toLowerCase())
                  setActiveValuesIfChanged(
                    getClusterUUIDsFromThemeName(themeName, label),
                  )
                  setSubthemeValue(label)
                }
              }}
              $active={isSelected}
            >
              <SubthemeLabel>{label}</SubthemeLabel>
            </ProductItem>
          )
        })}
      </ProductItemContainer>
    </Container>
  )
}

export default ThemeSelector

const Container = styled.div`
  margin-top: 6px;
  margin-bottom: 6px;
`

const ProductItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
`

const ProductItem = styled.div<{ $active: boolean }>`
  padding: 6px 12px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? pageBackgroundGrey : '#ffffff')};
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? buttonBlue : cardBorderGrey)};
  min-height: 44px;
  display: flex;
  align-items: center;
  transition:
    border-color 120ms ease,
    background-color 120ms ease,
    box-shadow 120ms ease;
  &:hover {
    background: ${pageBackgroundGrey};
    border-color: ${buttonBlue};
  }
`

const SubthemeLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 12px;
  line-height: 1.35;
`
